'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode } from 'react';
import { QueryApi } from '@/features/query/api';
import { PAGINATION_LIMIT } from '@/shared/config/constants/api';
import { EmptyContainer } from '@/shared/ui/empty-container';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import { OrdersHistoryModalOrder } from './orders-history-modal-order';
import './orders-history-modal.scss';

type Props = ModalProps & {
    children?: ReactNode;
};

type TitleProps = ComponentProps<typeof Layout>;

const OrdersHistoryModalTitle = ({ media, ...props }: TitleProps) => {
    const t = useTranslations();
    return (
        <Layout element={Modal.Title} media={media} data-testid="orders-history-modal-title" {...props}>
            {t('ORDERS_HISTORY_MODAL.TITLE')}
        </Layout>
    );
};

const OrdersHistoryModalContent = () => {
    const t = useTranslations();
    // TODO: Infinite scroll
    const ordersQuery = useQuery(QueryApi.Customer.queries.Orders({ offset: 0, limit: PAGINATION_LIMIT }));

    if (ordersQuery.data?.total === 0) {
        return (
            <EmptyContainer
                icon={(svgProps) => <Icon name="cart" {...svgProps} />}
                text={t('ORDERS_HISTORY_MODAL.EMPTY_MESSAGE')}
            />
        );
    }

    return (
        <>
            {ordersQuery.data?.orders.map((order) => (
                <OrdersHistoryModalOrder
                    key={order.orderId}
                    orderId={order.orderId}
                    dateTime={order.createdAt}
                    orderNumber={order.scopedNumber}
                    address={order.address}
                    priceValue={{ amount: Number(order.finalPrice), currency: order.currency }}
                />
            ))}
        </>
    );
};

export const OrdersHistoryModalWrapper = ({ children, ...props }: Props) => {
    return (
        <Modal
            {...props}
            wrapperProps={{
                className: 'orders-history-modal',
                containerProps: {
                    className: 'orders-history-modal__container',
                },
                'data-testid': 'orders-history-modal',
            }}
        >
            {children}
        </Modal>
    );
};

export {
    OrdersHistoryModalWrapper as Wrapper,
    OrdersHistoryModalTitle as Title,
    OrdersHistoryModalContent as Content,
};

export { OrdersHistoryModalWrapper as OrdersHistoryModal };
